/**
 * CSV Import Form component with file upload and validation feedback
 * Using React Hook Form
 */

import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useImportData } from '@/services/api/importExport'
import { useQueryClient } from '@tanstack/react-query'
import { assetKeys } from '@/services/api/assets'
import { saleKeys } from '@/services/api/sales'
import { ImportErrorReport } from './ImportErrorReport'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { toast } from 'sonner'
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Card, CardContent } from '@/components/ui/card'

const csvImportFormSchema = z.object({
  file: z.instanceof(File, { message: 'Vui lòng chọn file CSV' })
    .refine((file) => file.type === 'text/csv' || file.name.endsWith('.csv'), {
      message: 'File phải có định dạng CSV',
    }),
})

type CSVImportFormValues = z.infer<typeof csvImportFormSchema>

interface CSVImportFormProps {
  type: 'assets' | 'sales'
  onSuccess?: () => void
  onError?: (error: string) => void
}

export function CSVImportForm({ type, onSuccess, onError }: CSVImportFormProps) {
  const importMutation = useImportData()
  const queryClient = useQueryClient()

  const form = useForm<CSVImportFormValues>({
    resolver: zodResolver(csvImportFormSchema),
    defaultValues: {
      file: undefined as any,
    },
  })

  const onSubmit = async (data: CSVImportFormValues) => {
    try {
      const result = await importMutation.mutateAsync({ file: data.file, type })
      
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: assetKeys.lists() })
      queryClient.invalidateQueries({ queryKey: saleKeys.lists() })

      if (result.failed === 0) {
        onSuccess?.()
        form.reset()
        toast.success(`Import thành công: ${result.success} bản ghi`)
      } else {
        const message = `Import hoàn tất với ${result.failed} lỗi trong ${result.success + result.failed} bản ghi`
        toast.warning(message)
        onError?.(message)
      }
    } catch (error) {
      console.error('Import failed:', error)
      const errorMessage = error instanceof Error ? error.message : 'Import thất bại'
      toast.error(errorMessage)
      onError?.(errorMessage)
      form.setError('root', { message: errorMessage })
    }
  }

  return (
    <div>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {form.formState.errors.root && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <p className="text-red-700 text-sm">{form.formState.errors.root.message}</p>
            </CardContent>
          </Card>
        )}

        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="csv-import-file">
              Chọn file CSV
            </FieldLabel>
            <Input
              type="file"
              id="csv-import-file"
              accept=".csv"
              onChange={(e) => {
                const file = e.target.files?.[0] || null
                if (file) {
                  form.setValue('file', file, { shouldValidate: true })
                }
              }}
              aria-invalid={!!form.formState.errors.file}
            />
            <FieldDescription>
              File CSV phải có các cột bắt buộc theo định dạng chuẩn
            </FieldDescription>
            {form.formState.errors.file && (
              <FieldError errors={[form.formState.errors.file]} />
            )}
          </Field>
        </FieldGroup>

        <div className="space-y-3">
          {importMutation.isPending && (
            <Progress value={100} className="w-full" />
          )}
          <Button
            type="submit"
            className="w-full min-h-[44px]"
            disabled={!form.watch('file') || importMutation.isPending}
          >
            {importMutation.isPending ? 'Đang import...' : `Import ${type === 'assets' ? 'Tài sản' : 'Giao dịch Bán'}`}
          </Button>
        </div>
      </form>

      {importMutation.data && importMutation.data.errors.length > 0 && (
        <div className="mt-6">
          <ImportErrorReport errors={importMutation.data.errors} />
        </div>
      )}
    </div>
  )
}
